import axios, { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios'
import {
    BillingBillableDeliveryNoteSearchGridRequest,
    BillingCaseSearchGridRequest,
    BillingPatientSearchGridRequest
} from '@/api/services/billing/billing.dto'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { Nullable } from '@/shared/types/app.types'
import {
    InvoiceReceiptPatchRequest, InvoiceReceiptResponse
} from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.dto'

class BillingInvoiceReceiptService {
    async delete(
        laboratoryId: number,
        invoiceReceiptId: number
    ): Promise<void> {
        const response =
            await axios.delete<void>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}`)
        return response.data
    }

    async get(
        laboratoryId: number,
        invoiceReceiptId: number,
        cancelToken?: CancelToken
    ): Promise<InvoiceReceiptResponse> {
        const response =
            await axios.get<InvoiceReceiptResponse>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}`,
                { cancelToken })
        return response.data
    }

    async patch(
        laboratoryId: number,
        invoicePatchRequest: InvoiceReceiptPatchRequest
    ): Promise<EntityIdentifierWithErrorResponse[]> {
        const response =
            await axios.patch<EntityIdentifierWithErrorResponse[]>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts`,
                invoicePatchRequest)
        return response.data
    }

    async createFromInvoice(
        laboratoryId: number,
        invoiceId: number
    ): Promise<InvoiceReceiptResponse> {
        const response =
            await axios.post<InvoiceReceiptResponse>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/invoice`,
                invoiceId,
                { headers: { 'Content-Type': 'application/json' } })
        return response.data
    }
    async createFromAppliedBillableConcepts(
        laboratoryId: number,
        appliedBillableConceptIds: number[]
    ): Promise<InvoiceReceiptResponse> {
        const response =
            await axios.post<InvoiceReceiptResponse>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/appliedBillableConcepts`,
                appliedBillableConceptIds)
        return response.data
    }

    async paginator(
        filterModel: Nullable<CommonFilterModel>,
        params: Nullable<CommonParams>,
        laboratoryId: number
    ): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceReceiptResponse>>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts`,
                { ...filterModel },
                { params })
        return response.data
    }
    async export(
        laboratoryId: number,
        gridColumns?: ReportGridRequest,
        params?: ExportCommonParams
    ): Promise<AxiosResponse<Blob>> {
        return axios.put<Blob>(
            `/billing/laboratories/${laboratoryId}/invoiceReceipts/export`,
            { ...gridColumns },
            { params, responseType: 'arraybuffer' })
    }

    async searchPatient(
        laboratoryId: number,
        request: BillingPatientSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceReceiptResponse>>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/patient`,
                request,
                { params })
        return response.data
    }
    async searchCase(
        laboratoryId: number,
        request: BillingCaseSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceReceiptResponse>>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/case`,
                request,
                { params })
        return response.data
    }
    async searchBillableDeliveryNote(
        laboratoryId: number,
        request: BillingBillableDeliveryNoteSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceReceiptResponse>>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/billableDeliveryNote`,
                request,
                { params })
        return response.data
    }

    async addConcepts(
        laboratoryId: number,
        invoiceReceiptId: number,
        appliedBillableConceptIds: number[]
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}/addConceptsByReceiptInvoice`,
                appliedBillableConceptIds)
        return response.data
    }
    async addAllConcepts(
        laboratoryId: number,
        invoiceReceiptId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}/addConceptsByReceiptInvoice`)
        return response.data
    }

    async removeConcepts(
        laboratoryId: number,
        invoiceReceiptId: number,
        appliedBillableConceptIds: number[]
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}/removeConcepts`,
                appliedBillableConceptIds)
        return response.data
    }
    async removeAllConcepts(
        laboratoryId: number,
        invoiceReceiptId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}/removeAllConcepts`)
        return response.data
    }

    async verify(
        laboratoryId: number,
        invoiceReceiptId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}/verify`)
        return response.data
    }

    async getReport(
        laboratoryId: number,
        invoiceReceiptId: number,
        encoded: boolean
    ): Promise<Blob> {
        const config: AxiosRequestConfig = {
            params: { encoded },
            headers: { type: 'application/pdf' },
            responseType: encoded ? 'text' : 'arraybuffer'
        }
        const response =
            await axios.put<Blob>(
                `/billing/laboratories/${laboratoryId}/invoiceReceipts/${invoiceReceiptId}/report`,
                undefined,
                config)
        return response.data
    }
}
export const billingInvoiceReceiptService = new BillingInvoiceReceiptService()
